import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNotifications } from '@/contexts/NotificationContext';
// DTC-L: route network-signal `acknowledged_at` write through clock.
import { clock } from '@/utils/clock';

export interface NetworkSignal {
  id: string;
  organization_id: string;
  scope_type: string;
  scope_id: string;
  shop_id: string | null;
  signal_type: string;
  status: 'active' | 'acknowledged' | 'resolved';
  severity: 'high' | 'medium' | 'low';
  priority_score: number;
  title: string;
  summary: string;
  reasons: string[];
  first_detected_at: string;
  last_detected_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  resolved_at: string | null;
  created_at: string;
}

export interface RecommendationForSync {
  signal_type: string;
  shop_id?: string;
  scope_id?: string;
  severity: string;
  priority_score: number;
  title: string;
  summary: string;
  reasons: string[];
}

export function useNetworkSignals() {
  const [signals, setSignals] = useState<NetworkSignal[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { addNotification } = useNotifications();

  const activeCount = signals.filter((s) => s.status === 'active').length;

  const loadSignals = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('network_signals')
        .select('*')
        .in('status', ['active', 'acknowledged'])
        .order('priority_score', { ascending: false });

      if (error) {
        console.error('[useNetworkSignals] Load error:', error);
        return;
      }

      setSignals((data ?? []).map((d: any) => ({
        ...d,
        reasons: Array.isArray(d.reasons) ? d.reasons : [],
      })));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSignals();
  }, [loadSignals]);

  const syncSignals = useCallback(async (recommendations: RecommendationForSync[]) => {
    try {
      const payload = recommendations.map((r) => ({
        signal_type: r.signal_type,
        shop_id: r.shop_id ?? null,
        scope_id: r.scope_id ?? null,
        severity: r.severity,
        priority_score: r.priority_score,
        title: r.title,
        summary: r.summary,
        reasons: r.reasons,
      }));

      const { data, error } = await supabase.rpc('sync_network_signals', {
        _recommendations: payload as any,
      });

      if (error) {
        console.error('[useNetworkSignals] Sync error:', error);
        return;
      }

      // Fire notifications for new high-severity signals
      const result = data as any;
      const newIds: string[] = result?.new_signal_ids ?? [];

      if (newIds.length > 0) {
        // Find matching recommendations to get titles
        const highSevNew = recommendations.filter(
          (r) => r.severity === 'high'
        );
        if (highSevNew.length > 0) {
          addNotification({
            type: 'warning',
            title: 'New High-Priority Signal',
            message: `${newIds.length} new high-priority signal(s) detected across your scope.`,
            targetRoles: ['OWNER', 'MANAGER'],
          });
        }
      }

      // Reload signals to reflect changes
      await loadSignals();
    } catch (err) {
      console.error('[useNetworkSignals] Sync exception:', err);
    }
  }, [loadSignals, addNotification]);

  const acknowledgeSignal = useCallback(async (signalId: string) => {
    try {
      const { error } = await supabase.rpc('acknowledge_network_signal', {
        _signal_id: signalId,
      });

      if (error) {
        console.error('[useNetworkSignals] Acknowledge error:', error);
        return;
      }

      // Optimistic update
      setSignals((prev) =>
        prev.map((s) =>
          s.id === signalId
            ? { ...s, status: 'acknowledged' as const, acknowledged_at: clock.now().toISOString() }
            : s
        )
      );
    } catch (err) {
      console.error('[useNetworkSignals] Acknowledge exception:', err);
    }
  }, []);

  // Dismiss = soft acknowledge (per spec)
  const dismissSignal = useCallback(async (signalId: string) => {
    await acknowledgeSignal(signalId);
  }, [acknowledgeSignal]);

  return {
    signals,
    activeCount,
    isLoading,
    syncSignals,
    acknowledgeSignal,
    dismissSignal,
    loadSignals,
  };
}
